import {
  AppModuleCode,
  PermissionCode,
  TicketStatus,
  ticketStatusActionLabel,
  type AssignTicketRequest,
  type TicketCommentFormValues,
  type TicketDetail,
} from '@gb-ticket/shared-types';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import {
  ChevronDownIcon,
  ClipboardListIcon,
  FileTextIcon,
  HistoryIcon,
  Loader2Icon,
  MessagesSquareIcon,
  PaperclipIcon,
  UserRoundIcon,
  UsersIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TicketApprovalPanel } from '@/products/helpdesk/features/approvals/ticket-approval-panel';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useModule } from '@/features/auth/use-module';
import { TicketCsatPanel } from '@/products/helpdesk/features/csat/ticket-csat-panel';
import { TicketKbPanel } from '@/products/helpdesk/features/kb/ticket-kb-panel';
import { TicketAssetsPanel } from '@/products/helpdesk/features/assets/ticket-assets-panel';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import { TicketRemoteSupportPanel } from '@/products/helpdesk/features/remote-support/ticket-remote-support-panel';
import { TicketAttachments } from '@/products/helpdesk/features/tickets/ticket-attachments';
import {
  TicketAiAssistPanel,
  type TicketCommentDraft,
} from '@/products/helpdesk/features/tickets/ticket-ai-assist-panel';
import { AiSetBadge, TicketAiPanel } from '@/products/helpdesk/features/tickets/ticket-ai-panel';
import {
  ChangeRiskBadge,
  MajorIncidentBadge,
  PriorityBadge,
  ProblemLifecycleBadge,
  StatusBadge,
  TypeBadge,
} from '@/products/helpdesk/features/tickets/ticket-badges';
import { TicketComments } from '@/products/helpdesk/features/tickets/ticket-comments';
import { TicketIncidentPanel } from '@/products/helpdesk/features/tickets/ticket-incident-panel';
import { TicketChangePanel } from '@/products/helpdesk/features/tickets/ticket-change-panel';
import { TicketProblemPanel } from '@/products/helpdesk/features/tickets/ticket-problem-panel';
import { TicketSectionCard } from '@/products/helpdesk/features/tickets/ticket-section-card';
import { SlaCountdown } from '@/products/helpdesk/features/tickets/sla-countdown';
import { splitTicketTransitions } from '@/products/helpdesk/features/tickets/ticket-status-actions';
import { TicketTimeline } from '@/products/helpdesk/features/tickets/ticket-timeline';
import {
  useAddTicketComment,
  useAssignTicket,
  useChangeTicketStatus,
  useTicketAttachments,
  useTicketComments,
  useTicketHistory,
  useTicketLookups,
  useUploadTicketAttachment,
} from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime, formatEuro } from '@/lib/format';
import { PersonChip } from '@/platform/components/person-chip';
import { cn } from '@/lib/utils';

const NONE = 'none';

type DetailTab = 'conversation' | 'details' | 'actions';

const DETAIL_TABS: { id: DetailTab; label: string }[] = [
  { id: 'conversation', label: 'Gespräch' },
  { id: 'details', label: 'Details' },
  { id: 'actions', label: 'Aktionen' },
];

function pane(tab: DetailTab, current: DetailTab) {
  return tab === current ? undefined : 'max-lg:hidden';
}

export function TicketDetailView({ ticket }: { ticket: TicketDetail }) {
  const copy = useVoiceCopy();
  const ref = String(ticket.number);
  const me = useMe();
  const paths = useAppPaths();
  const lookups = useTicketLookups();
  const history = useTicketHistory(ref);
  const comments = useTicketComments(ref);
  const attachments = useTicketAttachments(ref);
  const changeStatus = useChangeTicketStatus(ref);
  const assignTicket = useAssignTicket(ref);
  const addComment = useAddTicketComment(ref);
  const uploadAttachment = useUploadTicketAttachment(ref);

  const canAssign = hasPermission(me.data, PermissionCode.TICKETS_ASSIGN);
  const canComment = hasPermission(me.data, PermissionCode.TICKETS_COMMENT);
  const canNoteInternal = hasPermission(me.data, PermissionCode.TICKETS_NOTE_INTERNAL);
  const queuesOn = useModule(AppModuleCode.QUEUES);
  const approvalsOn = useModule(AppModuleCode.APPROVALS);
  const csatOn = useModule(AppModuleCode.CSAT);
  const remoteOn = useModule(AppModuleCode.REMOTE_SUPPORT);
  const aiOn = useModule(AppModuleCode.AI);
  const incidentsOn = useModule(AppModuleCode.INCIDENTS);
  const problemsOn = useModule(AppModuleCode.PROBLEMS);
  const changesOn = useModule(AppModuleCode.CHANGES);
  const knowledgeOn = useModule(AppModuleCode.KNOWLEDGE);
  const assetsOn = useModule(AppModuleCode.ASSETS);
  const slaOn = useModule(AppModuleCode.SLA);

  const [tab, setTab] = useState<DetailTab>('conversation');
  const [assigneeId, setAssigneeId] = useState(ticket.assignee?.id ?? NONE);
  const [queueId, setQueueId] = useState(ticket.queue?.id ?? NONE);
  const [reason, setReason] = useState('');
  const [commentDraft, setCommentDraft] = useState<TicketCommentDraft | null>(null);

  async function onStatus(status: TicketDetail['allowedTransitions'][number]) {
    try {
      await changeStatus.mutateAsync({ status });
      toast.success(`Status: ${ticketStatusActionLabel(ticket.status, status)}`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Statuswechsel fehlgeschlagen.',
      );
    }
  }

  async function onAssign() {
    const body: AssignTicketRequest = {
      assigneeId: assigneeId === NONE ? null : assigneeId,
      queueId: queueId === NONE ? null : queueId,
      reason: reason.trim() || undefined,
    };
    try {
      await assignTicket.mutateAsync(body);
      toast.success('Zuweisung gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Zuweisung fehlgeschlagen.');
    }
  }

  async function onComment(values: TicketCommentFormValues) {
    try {
      await addComment.mutateAsync(values);
      toast.success(values.isInternal ? 'Interne Notiz gespeichert.' : 'Kommentar gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Kommentar fehlgeschlagen.');
    }
  }

  const statusToolbar = (
    <TicketStatusToolbar
      ticket={ticket}
      pending={changeStatus.isPending}
      onStatus={(status) => void onStatus(status)}
    />
  );

  return (
    <section className="space-y-4">
      <div className="sticky top-14 z-30 -mx-4 border-b bg-card px-4 py-2">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <p className="shrink-0 text-sm font-medium text-muted-foreground">
            {paths.isPortal ? `Anfrage #${ticket.number}` : `Ticket #${ticket.number}`}
          </p>
          <h1 className="min-w-0 flex-1 truncate text-base font-semibold text-primary">
            {ticket.subject}
          </h1>
          <StatusBadge status={ticket.status} />
          <div className="flex flex-wrap items-center gap-2">{statusToolbar}</div>
        </div>
      </div>

      <div>
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 h-8 text-muted-foreground hover:text-foreground"
        >
          <Link to={paths.tickets}>
            {paths.isPortal ? '← Zurück zu meinen Anfragen' : '← Zurück zur Übersicht'}
          </Link>
        </Button>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <PriorityBadge priority={ticket.priority} />
          {ticket.ai.prioritySet && <AiSetBadge />}
          <TypeBadge type={ticket.type} />
          {ticket.isMajor && <MajorIncidentBadge />}
          {ticket.problemLifecycle && <ProblemLifecycleBadge lifecycle={ticket.problemLifecycle} />}
          {ticket.changeRisk && <ChangeRiskBadge risk={ticket.changeRisk} />}
          {ticket.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Ticketbereiche"
        className="grid grid-cols-3 rounded-lg bg-muted p-1 lg:hidden"
      >
        {DETAIL_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={cn(
              'rounded-md px-2 py-2 text-sm',
              tab === item.id
                ? 'bg-card font-medium text-foreground shadow-sm'
                : 'text-muted-foreground',
            )}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-5">
          <div className={cn(pane('details', tab))}>
            <TicketSectionCard
              tone="navy"
              icon={FileTextIcon}
              title="Beschreibung"
              description={`Erstellt am ${formatDateTime(ticket.createdAt)} von ${ticket.createdBy.displayName}`}
            >
              <div
                className="ticket-prose"
                dangerouslySetInnerHTML={{ __html: ticket.description }}
              />
            </TicketSectionCard>
          </div>

          <div className={cn(pane('conversation', tab))}>
            <TicketSectionCard
              tone="navy"
              icon={MessagesSquareIcon}
              title={canNoteInternal ? 'Kommentare und interne Notizen' : 'Nachrichten'}
              description={
                paths.isPortal
                  ? copy('helpdesk.ticket.messages.yours')
                  : 'Kunde links, IT rechts. Interne Notizen sieht nur die IT.'
              }
            >
              <TicketComments
                items={comments.data?.items ?? []}
                requesterId={ticket.requester.id}
                canComment={canComment}
                canNoteInternal={canNoteInternal}
                pending={addComment.isPending}
                onSubmit={onComment}
                draft={commentDraft}
                onDraftConsumed={() => setCommentDraft(null)}
              />
            </TicketSectionCard>
          </div>

          {!paths.isPortal && aiOn && (
            <div className={cn(pane('conversation', tab))}>
              <TicketAiAssistPanel
                ticketRef={ref}
                canComment={canComment}
                canNoteInternal={canNoteInternal}
                onApplyToEditor={(draft) => {
                  setCommentDraft(draft);
                  toast.success(
                    draft.isInternal
                      ? 'Vorschlag steht als interne Notiz — bitte prüfen und speichern.'
                      : 'Vorschlag steht im Kommentarfeld — bitte prüfen und senden.',
                  );
                }}
              />
            </div>
          )}

          <div className={cn(pane('details', tab))}>
            <TicketSectionCard
              tone="steel"
              icon={PaperclipIcon}
              title="Anhänge"
              description="Bilder und PDFs können in der Vorschau geöffnet werden."
            >
              <TicketAttachments
                ticketRef={ref}
                items={attachments.data?.items ?? []}
                canUpload={canComment}
                uploading={uploadAttachment.isPending}
                onUpload={async (file) => {
                  try {
                    await uploadAttachment.mutateAsync(file);
                    toast.success('Anhang hochgeladen.');
                  } catch (error) {
                    toast.error(
                      error instanceof ApiRequestError
                        ? error.message
                        : 'Der Anhang konnte nicht hochgeladen werden.',
                    );
                  }
                }}
              />
            </TicketSectionCard>
          </div>

          <div className={cn(pane('details', tab))}>
            <TicketSectionCard
              tone="muted"
              icon={HistoryIcon}
              collapsible
              defaultOpen={false}
              title={paths.isPortal ? 'Was bisher passiert ist' : 'Verlauf'}
              description={
                paths.isPortal
                  ? 'Statusänderungen in zeitlicher Reihenfolge.'
                  : 'Statusänderungen und Zuweisungen chronologisch.'
              }
              action={
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="border-primary/20 text-primary"
                >
                  <Link to={paths.ticketHistory(ref)}>
                    {paths.isPortal ? 'Alle Schritte anzeigen' : 'Vollständige Historie'}
                  </Link>
                </Button>
              }
            >
              <div className="space-y-4">
                {history.isPending && (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2Icon className="size-4 animate-spin" aria-hidden />
                    Verlauf wird geladen …
                  </p>
                )}
                {history.data && <TicketTimeline items={history.data.items} />}
              </div>
            </TicketSectionCard>
          </div>
        </div>

        <aside className="space-y-5 lg:sticky lg:top-32 lg:self-start">
          <div className={cn('space-y-5', pane('actions', tab))}>
            {!paths.isPortal && (
              <TicketSectionCard
                tone="navy"
                icon={UserRoundIcon}
                title="Zuweisung"
                description="Agent oder Warteschlange."
              >
                <div className="space-y-3">
                  <p className="rounded-md bg-brand-navy-soft/50 px-3 py-2 text-sm">
                    <span className="text-muted-foreground">Aktuell: </span>
                    <span className="inline-flex min-w-0 flex-wrap items-center gap-2 font-medium text-brand-navy">
                      {ticket.assignee ? (
                        <PersonChip
                          displayName={ticket.assignee.displayName}
                          profilePhotoUrl={ticket.assignee.profilePhotoUrl}
                        />
                      ) : (
                        'kein Agent'
                      )}
                      {ticket.queue ? <span>· {ticket.queue.name}</span> : null}
                    </span>
                  </p>
                  {canAssign && (
                    <>
                      <div className="space-y-1.5">
                        <Label htmlFor="assignee">Agent</Label>
                        <Select value={assigneeId} onValueChange={setAssigneeId}>
                          <SelectTrigger id="assignee" className="w-full">
                            <SelectValue placeholder="Kein Agent" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>Kein Agent</SelectItem>
                            {(lookups.data?.agents ?? []).map((agent) => (
                              <SelectItem key={agent.id} value={agent.id}>
                                {agent.displayName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {queuesOn && (
                        <div className="space-y-1.5">
                          <Label htmlFor="queue">Warteschlange</Label>
                          <Select value={queueId} onValueChange={setQueueId}>
                            <SelectTrigger id="queue" className="w-full">
                              <SelectValue placeholder="Keine Warteschlange" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE}>Keine Warteschlange</SelectItem>
                              {(lookups.data?.queues ?? []).map((queue) => (
                                <SelectItem key={queue.id} value={queue.id}>
                                  {queue.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="space-y-1.5">
                        <Label htmlFor="reason">Begründung (optional)</Label>
                        <Input
                          id="reason"
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={() => void onAssign()}
                        disabled={assignTicket.isPending}
                      >
                        {assignTicket.isPending && (
                          <Loader2Icon className="animate-spin" aria-hidden />
                        )}
                        Zuweisen
                      </Button>
                    </>
                  )}
                </div>
              </TicketSectionCard>
            )}

            <TicketSectionCard
              tone="steel"
              icon={ClipboardListIcon}
              title={paths.isPortal ? 'Nächster Schritt' : 'Status'}
              description={statusCardDescription(ticket, paths.isPortal, copy)}
            >
              <div className="flex flex-col gap-2">
                <StatusLifecycleNotes ticket={ticket} />
                <div className="flex flex-col gap-2 lg:hidden">{statusToolbar}</div>
                {ticket.lifecycle.followUpSuggested ? (
                  <Button asChild type="button">
                    <Link to={`${paths.ticketNew}?folgeVon=${ticket.number}`}>
                      Neues Ticket zu diesem Vorgang
                    </Link>
                  </Button>
                ) : null}
              </div>
            </TicketSectionCard>

            {!paths.isPortal && slaOn && <SlaCountdown sla={ticket.sla} />}

            {(approvalsOn || ticket.approval?.canDecide) && ticket.approval && (
              <TicketApprovalPanel ticketRef={ref} approval={ticket.approval} />
            )}

            {remoteOn && <TicketRemoteSupportPanel key={`${ticket.id}-remote`} ticket={ticket} />}

            {!paths.isPortal && aiOn && <TicketAiPanel ticket={ticket} />}

            {incidentsOn && (
              <TicketIncidentPanel
                key={`${ticket.id}-incident-${ticket.updatedAt}`}
                ticket={ticket}
              />
            )}

            {problemsOn && (
              <TicketProblemPanel
                key={`${ticket.id}-problem-${ticket.updatedAt}`}
                ticket={ticket}
              />
            )}

            {changesOn && (
              <TicketChangePanel key={`${ticket.id}-change-${ticket.updatedAt}`} ticket={ticket} />
            )}

            {!paths.isPortal && knowledgeOn && (
              <TicketKbPanel ticketId={ticket.id} ticketNumber={ticket.number} />
            )}

            {!paths.isPortal && assetsOn && (
              <TicketAssetsPanel ticketId={ticket.id} ticketNumber={ticket.number} />
            )}

            {csatOn && (
              <TicketCsatPanel key={`${ticket.id}-csat-${ticket.status}`} ticket={ticket} />
            )}
          </div>

          <div className={cn(pane('details', tab))}>
            <TicketSectionCard
              tone="muted"
              icon={UsersIcon}
              title={paths.isPortal ? 'Details' : 'Beteiligte'}
            >
              <div className="space-y-2.5 text-sm">
                <p>
                  <span className="text-muted-foreground">
                    {paths.isPortal ? copy('helpdesk.ticket.from-you') : 'Anforderer: '}
                  </span>
                  <PersonChip
                    className="align-middle font-medium"
                    displayName={ticket.requester.displayName}
                    profilePhotoUrl={ticket.requester.profilePhotoUrl}
                  />
                </p>
                <p>
                  <span className="text-muted-foreground">Kategorie: </span>
                  <span className="font-medium">
                    {ticket.category
                      ? `${ticket.category.parentName ? `${ticket.category.parentName} / ` : ''}${ticket.category.name}`
                      : '—'}
                  </span>
                  {ticket.ai.categorySet && (
                    <span className="ml-2 inline-flex align-middle">
                      <AiSetBadge />
                    </span>
                  )}
                </p>
                {ticket.estimatedValueCents != null && (
                  <p>
                    <span className="text-muted-foreground">Geschätzter Wert: </span>
                    <span className="font-medium">{formatEuro(ticket.estimatedValueCents)}</span>
                  </p>
                )}
                <p>
                  <span className="text-muted-foreground">Aktualisiert: </span>
                  {formatDateTime(ticket.updatedAt)}
                </p>
              </div>
            </TicketSectionCard>
          </div>
        </aside>
      </div>
    </section>
  );
}

function TicketStatusToolbar({
  ticket,
  pending,
  onStatus,
}: {
  ticket: TicketDetail;
  pending: boolean;
  onStatus: (status: TicketDetail['allowedTransitions'][number]) => void;
}) {
  const { primary, rest } = splitTicketTransitions(ticket.status, ticket.allowedTransitions);
  if (!primary) {
    return null;
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        className="bg-brand-navy text-white hover:bg-brand-navy/90 focus-visible:ring-brand-navy/30"
        disabled={pending}
        onClick={() => onStatus(primary)}
      >
        {pending && <Loader2Icon className="animate-spin" aria-hidden />}
        {ticketStatusActionLabel(ticket.status, primary)}
      </Button>
      {rest.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="outline" disabled={pending}>
              Weitere
              <ChevronDownIcon className="size-4" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {rest.map((status) => (
              <DropdownMenuItem key={status} onSelect={() => onStatus(status)}>
                {ticketStatusActionLabel(ticket.status, status)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

function StatusLifecycleNotes({ ticket }: { ticket: TicketDetail }) {
  const paths = useAppPaths();
  return (
    <>
      {ticket.status === TicketStatus.RESOLVED && ticket.lifecycle.autoCloseAt ? (
        <p className="text-sm text-muted-foreground">
          Ohne Rückmeldung schließen wir diese Anfrage automatisch am{' '}
          {formatDateTime(ticket.lifecycle.autoCloseAt)}.
        </p>
      ) : null}
      {ticket.status === TicketStatus.CLOSED &&
      ticket.lifecycle.canReopen &&
      ticket.lifecycle.reopenUntil ? (
        <p className="text-sm text-muted-foreground">
          Wiedereröffnung möglich bis {formatDateTime(ticket.lifecycle.reopenUntil)}.
        </p>
      ) : null}
      {ticket.lifecycle.followUpOf ? (
        <p className="text-sm">
          Folgeticket zu{' '}
          <Link
            className="font-medium text-primary underline"
            to={paths.ticket(ticket.lifecycle.followUpOf.number)}
          >
            #{ticket.lifecycle.followUpOf.number}
          </Link>
        </p>
      ) : null}
      {ticket.lifecycle.followUps.length > 0 ? (
        <p className="text-sm">
          Folgetickets:{' '}
          {ticket.lifecycle.followUps.map((item, index) => (
            <span key={item.id}>
              {index > 0 ? ', ' : ''}
              <Link className="font-medium text-primary underline" to={paths.ticket(item.number)}>
                #{item.number}
              </Link>
            </span>
          ))}
        </p>
      ) : null}
      {ticket.allowedTransitions.length === 0 && !ticket.lifecycle.followUpSuggested ? (
        <p className="text-sm text-muted-foreground">Keine Statuswechsel möglich.</p>
      ) : null}
    </>
  );
}

function statusCardDescription(
  ticket: TicketDetail,
  isPortal: boolean,
  copy: (id: string) => string,
): string {
  if (ticket.status === TicketStatus.RESOLVED) {
    return isPortal
      ? copy('helpdesk.ticket.resolved.check')
      : 'Ohne Rückmeldung des Nutzers wird das Ticket automatisch geschlossen.';
  }
  if (ticket.lifecycle.canReopen && ticket.status === TicketStatus.CLOSED) {
    return isPortal
      ? copy('helpdesk.ticket.reopen.hint')
      : 'Wiedereröffnung ist nur innerhalb der konfigurierten Frist möglich.';
  }
  if (ticket.lifecycle.followUpSuggested) {
    return isPortal
      ? copy('helpdesk.ticket.reopen.follow-up')
      : 'Nach Ablauf der Frist nur noch über ein neues, verknüpftes Ticket.';
  }
  return isPortal
    ? 'Die IT kümmert sich um den nächsten Schritt.'
    : 'Nur erlaubte Übergänge werden angeboten.';
}
